import React, { useLayoutEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getBattlebotSlotCenterPixels } from '../components/BattlebotPortalOverlay';
import './WeaponEquipFlashOverlay.css';

const EQUIP_WEAPON_IMAGE = '/images/equip_weapon.png';
const DISPLAY_MS = 1000;

/**
 * equip_weapon.png centered on a deployment slot (matches unit card bounds when possible); ~1s on screen.
 */
export function WeaponEquipFlashOverlay({ viewerZone, slotIndex, flashKey, onComplete }) {
  const [center, setCenter] = useState(null);
  const doneRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const measuredRef = useRef(false);

  useLayoutEffect(() => {
    let cancelled = false;
    const measure = (attempt) => {
      const pt = getBattlebotSlotCenterPixels(viewerZone, slotIndex);
      if (pt && !cancelled) {
        measuredRef.current = true;
        setCenter(pt);
        return;
      }
      if (attempt < 22 && !cancelled) {
        requestAnimationFrame(() => measure(attempt + 1));
      }
    };
    setCenter(null);
    measuredRef.current = false;
    measure(0);
    return () => {
      cancelled = true;
    };
  }, [viewerZone, slotIndex, flashKey]);

  // If the slot never measures, still clear the parent flash list
  useLayoutEffect(() => {
    const id = window.setTimeout(() => {
      if (doneRef.current) return;
      if (measuredRef.current) return;
      doneRef.current = true;
      const fn = onCompleteRef.current;
      if (typeof fn === 'function') fn();
    }, 1300);
    return () => window.clearTimeout(id);
  }, [flashKey]);

  useLayoutEffect(() => {
    if (!center) return undefined;
    const id = window.setTimeout(() => {
      if (!doneRef.current) {
        doneRef.current = true;
        const fn = onCompleteRef.current;
        if (typeof fn === 'function') fn();
      }
    }, DISPLAY_MS);
    return () => window.clearTimeout(id);
  }, [center, flashKey]);

  if (!center) {
    return null;
  }

  const node = (
    <div
      className="weapon-equip-flash-root"
      aria-hidden
      style={{
        position: 'fixed',
        left: `${center.x}px`,
        top: `${center.y}px`,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 10003,
      }}
    >
      <img
        src={EQUIP_WEAPON_IMAGE}
        alt=""
        className="weapon-equip-flash-img"
        draggable={false}
      />
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(node, document.body);
  }
  return node;
}

export default WeaponEquipFlashOverlay;
